import { AppMetaData, ChallengeTeamEntity, UserProfileEntity, UserChallengeEntity, ChallengeTeamMemberEntity } from '../../../../meta-data/app-meta-data.service';
import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DwComponent, DwComponentType, DwMetaDataFormComponent, DwOrmDataService,
    DwOrmDataServiceToken, DwSecurityUserService, DwModalService, DwUIMetaDataConfig, DwUIMetaDataConfigToken } from '@devwareapps/devware-cap';
import { combineLatest, EMPTY, merge, Observable, of, Subject, Subscription, throwError } from 'rxjs';
import { catchError, debounceTime, map, startWith, switchMap, take, tap } from 'rxjs/operators';
import { createTeamProfilePreferences, updateTeamProfilePermission } from '../../models/team-profile.config';
import { ProfileRouteResolverService } from '../../../shared/services/profile-routing/profile-route-resolver.service';
import { ProfileDirectoryService } from '../../../shared/services/profile-routing/profile-directory.service';
import { canonicalSlug } from '../../../shared/services/profile-routing/profile-links';
import { TeamFormService } from '../../services/team-form.service';
import { AppPermissions } from '../../../../meta-data/app-permissions.enum';

import { CentralDataService, DisplayTeam } from '../../../streaker-central/services/central-data.service';
import { ChallengeContextService } from '../../../shared/contexts/challenge-context/challenge-context.service';

@DwComponent({ key: 'fitness-team-profile', name: 'Team profile',
    componentType: DwComponentType.pageComponent, isGlobal: true })
@Component({ selector: 'app-team-profile', templateUrl: './team-profile.component.html',
    styles: [`:host { display: block; } main { max-width: 1200px; padding: 1.5rem; margin: auto; }
      app-avatar-banner { display:block; margin-bottom:1.5rem; } .team-actions { display:flex; gap:8px; flex-wrap:wrap; }
      .captain-badge { padding:4px 9px; border-radius:999px; background:var(--dw-fitness-mint); color:var(--dw-fitness-green); font-size:12px; font-weight:600; }
      @media(max-width:620px) { .team-actions { flex-direction:column; } }`] })
export class TeamProfileComponent implements OnInit, OnDestroy {
    @ViewChild('form') form: DwMetaDataFormComponent;
    teamId = 0;
    teamData: ChallengeTeamEntity;
    previewData: ChallengeTeamEntity & {resolvedDisplay?: boolean};
    loading = true;
    error = '';
    canEdit = false;
    isCaptain = false;
    editing = false;
    // Remount identity-scoped CAP content on route reuse, including cache hits.
    formScope = '';
    trackFormScope(_index: number, scope: string): string { return scope; }
    get canAdministerAllTeams(): boolean {
        return this.security.hasPermission('Streaker_Team_CanAdminAll') || this.security.hasPermission(AppPermissions.entAdminCanAccess);
    }
    private permissionRequests = new Subscription();
    private reload$ = new Subject<void>();
    preferences = createTeamProfilePreferences(false, false);
    private subscriptions = new Subscription();

    constructor(private route: ActivatedRoute, private security: DwSecurityUserService,
        @Inject(DwOrmDataServiceToken) private orm: DwOrmDataService,
        private modal: DwModalService, @Inject(DwUIMetaDataConfigToken) private metaDataConfig: DwUIMetaDataConfig,
        private profileRoutes: ProfileRouteResolverService, private directory: ProfileDirectoryService,
        private teamForms: TeamFormService, private data: CentralDataService, private selection: ChallengeContextService) {}
    ngOnInit(): void {
        this.subscriptions.add(this.data.teamChanged$.pipe(debounceTime(0)).subscribe(change => {
            if (!this.teamData || (change.teamId && change.teamId !== this.teamId)) { return; }
            if (change.team && !change.removed) {
                if (change.team.resolvedDisplay) { this.displayData = change.team; }
                const raw = change.team.resolvedDisplay ? this.data.cacheValue<ChallengeTeamEntity[]>("team:" + this.teamId)?.[0] : change.team;
                if (raw) { Object.assign(this.teamData, raw); }
                this.form?.patchFormData(this.teamData);
            }
            if (change.removed && change.team) { this.error = 'This team is no longer available.'; return; }
            if (change.kind === 'membership' || change.member || change.team?.IsPrivateTeam === true || !change.teamId) {
                this.permissionRequests.unsubscribe(); this.permissionRequests = new Subscription();
                const checkedTeamId = this.teamId;
                const userId = this.security.securityContext?.ApplicationUser?.UserId;
                if (!userId) { return; }
                this.permissionRequests.add(this.loadUserProfiles(userId).pipe(switchMap(profiles => profiles.length === 1
                    ? this.loadCompletedEnrollments(profiles[0].UserProfileId, this.teamData.ChallengeDefinitionId) : of([])),
                    switchMap(enrollments => enrollments.length === 1 ? this.loadMemberships(this.teamId, enrollments[0].UserChallengeId) : of([])), take(1))
                    .subscribe(members => {
                        if (this.teamId !== checkedTeamId || this.security.securityContext?.ApplicationUser?.UserId !== userId) { return; }
                        this.isCaptain = members.some(member => member.IsTeamAdmin); this.canEdit = this.canAdministerAllTeams || this.isCaptain;
                        updateTeamProfilePermission(this.preferences, this.canEdit);
                        if (this.teamData.IsPrivateTeam && !members.length && !this.canAdministerAllTeams) { this.error = 'This team profile is available to its members.'; }
                    }, () => {}));
            }
        }));
        this.subscriptions.add(combineLatest([this.route.paramMap,
            this.security.securityContextChanged().pipe(startWith(null)), merge(this.reload$, this.data.changed$).pipe(startWith(null)),
            this.selection.challengeChange$]).pipe(switchMap(([params, , , selected]) => {
            this.permissionRequests.unsubscribe(); this.permissionRequests = new Subscription();
            this.loading = true; this.error = ''; this.canEdit = false; this.isCaptain = false; this.teamData = null;
            this.displayData = null;
            this.previewData = null;
            this.formScope = JSON.stringify([params.get('teamId'), params.get('slug'), params.get('challengeId'),
                selected.ChallengeDefinitionId, this.security.securityContext?.AuthenticatedUser?.UserId,
                this.security.securityContext?.ApplicationUser?.UserId]);
            const user = this.security.securityContext && this.security.securityContext.ApplicationUser;
            if (!user || !user.UserId) { this.error = 'Sign in to view this team.'; return of(null); }
            const challengeId = Number(params.get('challengeId')) || selected.ChallengeDefinitionId;
            const slugParam = params.get('slug');
            // Older one-segment numeric links identify a team by ID.
            const numericId = !params.has('challengeId') && /^\d+$/.test(slugParam || '') ? Number(slugParam) : Number(params.get('teamId'));
            if (params.has('slug') && !numericId && !challengeId) { return EMPTY; }
            const identity: Observable<Pick<ChallengeTeamEntity, 'ChallengeTeamId'>> = params.has('slug') && !numericId ? this.directory.get(challengeId, 'team').pipe(switchMap(directory => {
                const slug = canonicalSlug(params.get('slug'));
                const teams = directory.teams.filter(team => team.TeamSlug === slug);
                if (teams.length > 1) { throw new Error('Profile link is not unique.'); }
                this.previewData = teams.length === 1 && teams[0].IsPrivateTeam === false ? teams[0] : null;
                return teams.length ? of(teams[0]) : this.profileRoutes.resolveTeam(challengeId, slug);
            }))
                : of({ ChallengeTeamId: numericId });
            return identity.pipe(switchMap(resolved => {
                this.teamId = resolved.ChallengeTeamId;
                if (!Number.isSafeInteger(this.teamId) || this.teamId <= 0) { throw new Error('Invalid team link.'); }
                return this.loadTeam(this.teamId);
            }), switchMap(teams => {
                if (teams.length !== 1) { throw new Error('Team unavailable.'); }
                const team = teams[0];
                this.previewData = team.IsPrivateTeam === false ? this.displayData || team : null;
                return this.loadUserProfiles(user.UserId).pipe(switchMap(profiles => {
                    if (profiles.length !== 1) { return of({ team, memberships: [] }); }
                    return this.loadCompletedEnrollments(profiles[0].UserProfileId, team.ChallengeDefinitionId).pipe(switchMap(enrollments => {
                        if (enrollments.length !== 1) { return of({ team, memberships: [] }); }
                        return this.loadMemberships(this.teamId, enrollments[0].UserChallengeId).pipe(switchMap(memberships => of({ team, memberships })));
                    }));
                }), catchError(error => this.canAdministerAllTeams
                    ? of({ team, memberships: [] }) : throwError(error)));
            }), catchError(() => { this.error = 'Unable to load this team. Please reload.'; return of(null); }));
        })).subscribe(result => {
            if (result) {
                if (result.team.IsPrivateTeam === true && !result.memberships.length && !this.canAdministerAllTeams) {
                    this.error = 'This team profile is available to its members.';
                } else {
                    this.isCaptain = result.memberships.some(member => member.IsTeamAdmin === true);
                    this.canEdit = this.canAdministerAllTeams || this.isCaptain;
                    this.teamData = result.team;
                    this.preferences = createTeamProfilePreferences(this.canEdit, false, this.route.snapshot.queryParamMap?.get('tab'));
                    const challengeId = result.team.ChallengeDefinitionId;
                    this.subscriptions.add(this.data.definitions().pipe(take(1)).subscribe(definitions => {
                        if (this.teamData?.ChallengeTeamId !== result.team.ChallengeTeamId) { return; }
                        const definition = definitions.find(row => row.ChallengeDefinitionId === challengeId);
                        const start = Date.parse(definition?.ChallengeStartDate || '');
                        (this.preferences.section as any).config.activitiesStarted = Number.isFinite(start) && Date.now() >= start;
                    }, () => {}));
                }
            }
            this.loading = false;
        }));
    }
    displayData: DisplayTeam;
    saveImages = (avatarId: number, bannerId: number): Observable<void> => {
        if (!this.canEdit) { return throwError(new Error('Team editing is unavailable.')); }
        const entity = AppMetaData.ChallengeTeam.CreateEntity();
        entity.ChallengeTeamId = this.teamId;
        entity.TeamPictureMediaId = avatarId;
        entity.TeamBackgroundMediaId = bannerId;
        return this.orm.saveEntity(entity, false).pipe(tap(() => {
            this.directory.invalidate(); this.data.invalidate(); this.reload$.next();
        }), map(() => undefined));
    };
    private loadTeam(teamId: number): Observable<ChallengeTeamEntity[]> {
        return combineLatest([this.data.team(teamId), this.data.displayTeam(teamId)]).pipe(map(([raw, display]) => { this.displayData = display[0]; return raw; }));
    }
    private loadUserProfiles(userId: number): Observable<UserProfileEntity[]> {
        return this.data.userProfile(userId);
    }
    private loadCompletedEnrollments(profileId: number, challengeId: number): Observable<UserChallengeEntity[]> {
        return this.data.enrollment(profileId, challengeId).pipe(map(rows => rows.filter(row => row.IsRegistrationComplete === true)));
    }
    private loadMemberships(teamId: number, enrollmentId: number): Observable<ChallengeTeamMemberEntity[]> {
        return this.data.memberships(enrollmentId).pipe(map(rows => rows.filter(row => row.ChallengeTeamId === teamId)));
    }
    canNavigate(): Observable<boolean> {
        if (!this.form || !this.form.hasChanges()) { return of(true); }
        return this.modal.showConfirmDialog({
            title: this.metaDataConfig.formUnsavedChangesInfo?.title || 'Leave page?',
            message: this.metaDataConfig.formUnsavedChangesInfo?.message || 'You have unsaved changes. Are you sure you wish to leave?',
            useConfirmDefaults: true,
            showConfirmDialog: true,
        });
    }
    editTeam(): void {
        this.openEditor(false);
    }
    editStyle(): void {
        this.openEditor(true);
    }
    private openEditor(style: boolean): void {
        if (!this.canEdit || this.editing) { return; }
        this.editing = true;
        this.subscriptions.add((style ? this.teamForms.style(this.teamId) : this.teamForms.edit(this.teamId)).pipe(take(1)).subscribe(result => {
            this.editing = false;
            if (!result.cancelled && result.data?.ChallengeTeamId === this.teamId) {
                this.directory.invalidate();
                this.data.invalidate();
                this.reload$.next();
            }
        }, () => { this.editing = false; this.error = 'Unable to open the team form. Please try again.'; }));
    }
    ngOnDestroy(): void { this.subscriptions.unsubscribe(); this.permissionRequests.unsubscribe(); }
}
